/**
 * KpiStrip: the headline figures of the LAE Productivity dashboard (Schools mode) as one quiet strip,
 * a single card split by hairlines.
 *
 * Ports LAE `features/productivity/KpiStrip.jsx`. Deliberate differences:
 *  - No per-tile editing (drag to reorder, hide buttons, restore chips, `layout`/`stripId`). EMERGE's
 *    page layout manager works on whole blocks only; a person hides the whole block instead.
 *  - No rubric bands (accent, dot, band reason in the tooltip).
 *  - No click handlers: no tile opens a drill-down, so there is no chevron and no button role.
 *  - No definition tooltips. The label carries a native `title` with its own full text, because it may
 *    clamp to two lines.
 *  - Labels are sentence case (EMERGE header style), not uppercase.
 *  - `wrapLabels` is the only behaviour: both LAE callers passed it.
 *  - Half width is a `half` prop rather than LAE's descendant selector (`KPI_HALF` in SchoolsView): from
 *    the large breakpoint the tiles flow into as many 10rem columns as fit, and the last tile's
 *    stretch span is dropped.
 *
 * Column counts are capped per breakpoint by `cols` merged over the defaults (base 2, md 4, xl 8; lg
 * falls back to md). The default `cols` is the Schools hero rule (lg 4, xl 4, the same as
 * `SCHOOL_HERO_COLS` in `lae-hero.ts`), which every caller of this strip uses. The last tile stretches
 * over whatever its row leaves empty, so a wrapped row never leaves an empty slot.
 */
import { cx } from '@/lib/cx';
import { Skeleton } from '@/components/ui/Skeleton';
import { Sparkline } from './Sparkline';

export type KpiItem = {
  key: string;
  label: string;
  value: string;
  delta: number | null;
  spark?: number[] | (number | null)[];
  sparkLabels?: string[];
  sparkFormat?: (v: number) => string;
};

type KpiCols = { base?: number; md?: number; lg?: number; xl?: number };

// Static class strings so Tailwind can see every one of them.
const COLS = {
  base: ['', 'grid-cols-1', 'grid-cols-2'],
  md: ['', 'md:grid-cols-1', 'md:grid-cols-2', 'md:grid-cols-3', 'md:grid-cols-4'],
  lg: [
    '',
    'lg:grid-cols-1',
    'lg:grid-cols-2',
    'lg:grid-cols-3',
    'lg:grid-cols-4',
    'lg:grid-cols-5',
    'lg:grid-cols-6',
    'lg:grid-cols-7',
    'lg:grid-cols-8',
  ],
  xl: [
    '',
    'xl:grid-cols-1',
    'xl:grid-cols-2',
    'xl:grid-cols-3',
    'xl:grid-cols-4',
    'xl:grid-cols-5',
    'xl:grid-cols-6',
    'xl:grid-cols-7',
    'xl:grid-cols-8',
  ],
} as const;
const SPAN = {
  base: ['', 'col-span-1', 'col-span-2'],
  md: ['', 'md:col-span-1', 'md:col-span-2', 'md:col-span-3', 'md:col-span-4'],
  lg: [
    '',
    'lg:col-span-1',
    'lg:col-span-2',
    'lg:col-span-3',
    'lg:col-span-4',
    'lg:col-span-5',
    'lg:col-span-6',
    'lg:col-span-7',
    'lg:col-span-8',
  ],
  xl: [
    '',
    'xl:col-span-1',
    'xl:col-span-2',
    'xl:col-span-3',
    'xl:col-span-4',
    'xl:col-span-5',
    'xl:col-span-6',
    'xl:col-span-7',
    'xl:col-span-8',
  ],
} as const;
const HALF_GRID = 'lg:grid-cols-[repeat(auto-fill,minmax(10rem,1fr))]';
const HALF_SPAN = 'lg:col-span-1';

const MAX = { base: 2, md: 4, xl: 8 };
/** The Schools hero rule (mirrors `SCHOOL_HERO_COLS`): rows of four from the large breakpoint. */
const DEFAULT_COLS: KpiCols = { lg: 4, xl: 4 };

/** The last item stretches over whatever its row leaves empty. */
function lastSpan(n: number, cols: number): number {
  if (cols <= 0) return 1;
  const r = n % cols;
  return r === 0 ? 1 : cols - r + 1;
}

function pick(list: readonly string[], i: number): string {
  return list[Math.max(0, Math.min(i, list.length - 1))] ?? '';
}

/** Grid and last-tile classes for `n` tiles. */
function layoutClasses(n: number, cols: KpiCols | undefined, half: boolean) {
  const max = { ...MAX, ...(cols ?? DEFAULT_COLS) };
  const c = {
    base: Math.min(n, max.base ?? MAX.base),
    md: Math.min(n, max.md ?? MAX.md),
    lg: Math.min(n, max.lg ?? max.md ?? MAX.md),
    xl: Math.min(n, max.xl ?? MAX.xl),
  };
  const grid = cx(
    'grid -ml-px -mt-px',
    pick(COLS.base, c.base),
    pick(COLS.md, c.md),
    half ? HALF_GRID : pick(COLS.lg, c.lg),
    !half && pick(COLS.xl, c.xl),
  );
  const last = cx(
    pick(SPAN.base, lastSpan(n, c.base)),
    pick(SPAN.md, lastSpan(n, c.md)),
    half ? HALF_SPAN : pick(SPAN.lg, lastSpan(n, c.lg)),
    !half && pick(SPAN.xl, lastSpan(n, c.xl)),
  );
  return { grid, last };
}

const CELL = 'flex min-w-0 flex-col gap-2 border-l border-t border-border p-4';
const LABEL = 'min-h-[2lh] text-xs leading-snug font-medium text-fg-muted';
const VALUE = 'min-w-0 truncate text-2xl leading-tight font-semibold text-fg tabular-nums';
const BOTTOM = 'flex min-h-[28px] flex-wrap items-end justify-between gap-2';

function Delta({ value }: { value: number | null }) {
  if (value == null || !Number.isFinite(value) || Math.abs(value) < 0.5) return null;
  const up = value > 0;
  return (
    <span className="text-xs text-fg-muted tabular-nums">
      <span aria-hidden="true">{up ? '▲' : '▼'}</span>
      <span className="sr-only">{up ? 'up' : 'down'}</span> {Math.round(Math.abs(value))}%
    </span>
  );
}

function hasSparkData(spark: KpiItem['spark']): spark is (number | null)[] {
  return Array.isArray(spark) && spark.length > 1;
}

export function KpiStrip({
  items,
  loading = false,
  cols,
  half = false,
}: {
  items: KpiItem[];
  loading?: boolean;
  cols?: KpiCols;
  half?: boolean;
}) {
  const n = items.length;
  if (!n) return null;
  const { grid, last } = layoutClasses(n, cols, half);

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      {/* Each cell draws its own top and left hairline; the 1px offset hides the outer ones. */}
      <div className={grid}>
        {items.map((item, i) => (
          <div key={item.key} className={cx(CELL, i === n - 1 && last)}>
            <p className={LABEL} title={item.label}>
              <span className="line-clamp-2 break-words">{item.label}</span>
            </p>
            <p className={VALUE}>
              {loading ? <Skeleton className="my-1 h-6 w-2/3" /> : item.value}
            </p>
            {/* Wraps when a narrow cell cannot fit the delta beside the 96px sparkline. */}
            <div className={BOTTOM}>
              {loading ? <span /> : <Delta value={item.delta} />}
              {!loading && hasSparkData(item.spark) && (
                <span className="ml-auto max-w-full shrink-0">
                  <Sparkline
                    values={item.spark}
                    labels={item.sparkLabels}
                    formatValue={item.sparkFormat}
                    width={96}
                    height={28}
                    ariaLabel={`${item.label} trend`}
                  />
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The strip's shape with placeholder labels and values, for a first load with no items yet. */
export function KpiStripSkeleton({
  count,
  cols,
  half = false,
}: {
  count: number;
  cols?: KpiCols;
  half?: boolean;
}) {
  const n = Math.max(0, Math.floor(count));
  if (!n) return null;
  const { grid, last } = layoutClasses(n, cols, half);
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface" aria-hidden="true">
      <div className={grid}>
        {Array.from({ length: n }, (_, i) => (
          <div key={i} className={cx(CELL, i === n - 1 && last)}>
            <div className={LABEL}>
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="my-1 h-6 w-2/3" />
            <div className={BOTTOM} />
          </div>
        ))}
      </div>
    </div>
  );
}
